import { Flex, EmptyState } from '@pega/cosmos-react-core';
import React, { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { appState } from '../../../../interfaces/common';
import { updateLoaderDetails } from '../../../../redux/actions';
import CustomerTranscriptAndDetails from './CustomerTranscriptAndDetails';

export interface LiveSimulationProps {
  connectionId: string;
  socketRef: MutableRefObject<WebSocket | undefined>;
}

export interface CustomerDetailsProps {
  selectedCustomerData: any;
}

const LiveSimulation: React.FC<LiveSimulationProps> = ({ connectionId, socketRef }) => {
  const dispatch = useDispatch();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const [customerData, setCustomerData] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>({});
  const translate = useLocalTranslation();

  const connect = () => {
    dispatch(updateLoaderDetails(true));
    const accountId = connectionId?.split('_')[1];
    socketRef.current = new WebSocket(
      `wss://${process.env.REACT_APP_CUSTOMER_AI_SIMULATOR_WSS}/?session_id=${sessionId}&account_id=${accountId}`,
    );

    socketRef.current.onopen = () => {
      if (socketRef.current) {
        socketRef.current.send(JSON.stringify({ action: 'fetch_data' }));
      }
    };

    socketRef.current.onclose = (event: CloseEvent) => {
      console.log('connection closed');
    };

    // Error handler need to be updated
    socketRef.current.onerror = (event: Event) => {
      console.log('connection error');
      dispatch(updateLoaderDetails(false));
      if (window.navigator.onLine && socketRef.current?.readyState !== 3) {
        console.log(event);
      }
    };
  };

  useEffect(() => {
    if (!socketRef.current || ![0, 1].includes(socketRef.current?.readyState as number)) connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketRef]);

  const socketCB = useCallback(
    (event: MessageEvent) => {
      const response = JSON.parse(event.data);
      if (response.action === 'customers_list') {
        setCustomerData((prevCustomers) => [...prevCustomers, ...response.customers]);
        dispatch(updateLoaderDetails(false));
      } else if (response.action === 'update_customer') {
        setCustomerData((prevCustomers) => {
          const customerIndex = prevCustomers.findIndex((customer) => customer.customerId === response.customerId);
          let newCustomers = [...prevCustomers];
          newCustomers[customerIndex] = response.customer;
          return newCustomers;
        });
      } else if (response.action === 'delete_customer') {
        setCustomerData((prevState) =>
          prevState.filter((customer: any) => response.customerId !== customer?.customerId),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.onmessage = socketCB;
    }
  }, [socketRef, socketCB]);

  return (
    <Flex>
      {customerData?.length > 0 ? (
        <CustomerTranscriptAndDetails
          customerData={customerData}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
        />
      ) : (
        <EmptyState message={translate('no_customers_generated')} />
      )}
    </Flex>
  );
};

export default LiveSimulation;
